// Port of ios/Sources/ViewModels/ChannelListTabs.swift.
//
// The phone's bottom tabs and the iPad's rail — Inicio, Mensajes, Actividad, Guardados (user
// direction 2026-10-09, Slack's shape: every tab a PLACE, not a filter). Twin of Android's
// `ChannelListTabs`.
//
// What the old lenses did survives: Inicio is the whole sectioned list, Mensajes is the old Chats
// lens (conversations only, newest first), and "only unread" is a filter chip on the list itself
// so it narrows either list. Actividad and Guardados are panes of their own — `sections` returns
// nothing for them.
import { channelListSection, type ChannelListSection, type DisplayChannel } from './channelListDisplayModels';

export type ChannelListTab = 'home' | 'dms' | 'channels' | 'activity' | 'saved';

/** Swift's `CaseIterable.allCases`, in declaration order. */
export const allTabs: readonly ChannelListTab[] = ['home', 'dms', 'channels', 'activity', 'saved'];

/** Whether the tab shows the conversation list (and so the search field and the chips). */
export function isConversationList(tab: ChannelListTab): boolean {
  return tab === 'home' || tab === 'dms' || tab === 'channels';
}

/**
 * The red number and the plain dot each tab wears. Inicio holds everything, so its badge is
 * the total; Mensajes repeats the conversations' share, because that is the tab the reader
 * taps to answer them. Muted rows count toward neither.
 */
export interface TabBadges {
  homeMentions: number;
  homeUnread: boolean;
  dmsMentions: number;
  dmsUnread: boolean;
  channelsMentions: number;
  channelsUnread: boolean;
}

export function sections(all: readonly ChannelListSection[], tab: ChannelListTab): ChannelListSection[] {
  switch (tab) {
    case 'home':
      return [...all];
    case 'dms':
      // Newest first, whatever the category's sorting says (user direction 2026-08-29):
      // alphabetical DMs are a phone book, not an inbox.
      return rebuild(all, (rows) => rows.filter(isDirect).sort((a, b) => b.lastPostAt - a.lastPostAt));
    case 'channels':
      // The phone's other half (user 2026-10-09: "channels separated from dm in mobile
      // apps"): channels only, in the categories' own order.
      return rebuild(all, (rows) => rows.filter((r) => !isDirect(r)));
    case 'activity':
    case 'saved':
      return [];
  }
}

export function badges(all: readonly ChannelListSection[]): TabBadges {
  let homeMentions = 0;
  let dmsMentions = 0;
  let homeUnread = false;
  let dmsUnread = false;
  let channelsMentions = 0;
  let channelsUnread = false;
  const seen = new Set<string>();
  for (const row of all.flatMap((s) => s.rows)) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    if (row.isMuted) continue;
    homeMentions += row.mentionCount;
    homeUnread = homeUnread || row.isUnread;
    if (isDirect(row)) {
      dmsMentions += row.mentionCount;
      dmsUnread = dmsUnread || row.isUnread;
    } else {
      channelsMentions += row.mentionCount;
      channelsUnread = channelsUnread || row.isUnread;
    }
  }
  return { homeMentions, homeUnread, dmsMentions, dmsUnread, channelsMentions, channelsUnread };
}

/**
 * The Unread chip's filter: only rows asking for attention, and only sections that keep some.
 * Muted rows stay out even when their counters drift forward — muting is total.
 */
export function unreadOnly(all: readonly ChannelListSection[]): ChannelListSection[] {
  const out: ChannelListSection[] = [];
  for (const section of all) {
    const rows = section.rows.filter((r) => (r.isUnread || r.mentionCount > 0) && !r.isMuted);
    if (rows.length === 0) continue;
    // Spread: a section may carry more than the memberwise fields (a type section's colour, §46).
    out.push({ ...section, collapsed: false, rows });
  }
  return out;
}

/**
 * Conversations that would light the Unread chip: unread or mentioning, never muted. Counted
 * over every section, so the chip says how many it shows when on and would show when off.
 */
export function unreadConversationCount(all: readonly ChannelListSection[]): number {
  const seen = new Set<string>();
  let count = 0;
  for (const row of all.flatMap((s) => s.rows)) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    if ((row.isUnread || row.mentionCount > 0) && !row.isMuted) count++;
  }
  return count;
}

function rebuild(
  all: readonly ChannelListSection[],
  pick: (rows: DisplayChannel[]) => DisplayChannel[],
): ChannelListSection[] {
  const out: ChannelListSection[] = [];
  for (const section of all) {
    const rows = pick([...section.rows]);
    if (rows.length === 0) continue;
    // Spread: a section may carry more than the memberwise fields (a type section's colour, §46).
    out.push({ ...section, rows });
  }
  return out;
}

function isDirect(row: DisplayChannel): boolean {
  return row.type === 'D' || row.type === 'G';
}

/** Namespace form, for `ChannelListTab.sections(...)` call sites. */
export const ChannelListTab = { allTabs, isConversationList, sections, badges, unreadOnly, unreadConversationCount } as const;
